import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import { useSelector } from 'store';
import { useFeature, useGrowthBook } from '@growthbook/growthbook-react';
import {
  App,
  AppPermission,
  appPermissionAccessLevels,
  appPermissionStates,
  DISABLED_TYPE,
  DisabledReason,
  PermissionScope,
} from 'types/apps';
import _ from 'lodash';
import {
  ALL_APP_IDS,
  ALL_APPS,
  ALL_FEATURE_IDS,
  ALL_VIEW_IDS,
  PARTS_V1_DEPRECATIONS,
} from 'constants/appConstants';
import {
  calculateAppPermissionState,
  calculateViewFeaturePermissionState,
  doesUserAccessLevelMeetRequiredAccessLevel,
  getGrowthbookValues,
} from 'utils/permissions';
import { useGetUserAccessControlsQuery } from 'store/slices/apiV1/accessEntitlements';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';
import { useIsCofactr } from 'hooks/useIsCofactr';
import { FeatureFlags } from 'types';
import { getPartsV2PermissionScope } from 'utils/functions';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import { skipToken } from '@reduxjs/toolkit/dist/query';

type AppAccessContextType = {
  appPermissions: { [key in ALL_APP_IDS]?: AppPermission };
  appsWithPermissions: { [key: string]: App };
  isLoading: boolean;
  hasAppPermission: (app: keyof typeof ALL_APP_IDS) => boolean;
  hasFeaturePermission: (feature: keyof typeof ALL_FEATURE_IDS) => boolean;
  isFeatureDisabled: ({
    feature,
    requiredPermissionLevel,
  }: {
    feature: keyof typeof ALL_FEATURE_IDS;
    requiredPermissionLevel?: keyof typeof appPermissionAccessLevels;
  }) => boolean;
  isPermissionDisabled: (scope?: PermissionScope) => boolean;
  getDisabledReason: (scope: PermissionScope) => DisabledReason;
};

enum VIEW_OR_FEATURE {
  VIEW,
  FEATURE,
}

export const AppAccessContext = createContext({} as AppAccessContextType);

export const useAppAccessContext = () => useContext(AppAccessContext);

export const AppAccessContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const tempParts2Ui = useFeature(FeatureFlags.tempParts2Ui).on;

  const { data: user, isLoading: isLoadingUser } = useGetUserQuery();
  const { isCofactr, viewAsStaff } = useIsCofactr();
  const { data: userAccessControls, isLoading: isLoadingUserAccessControls } =
    useGetUserAccessControlsQuery(undefined);
  const { activeOrgId, blended } = useSelector((state) => state.org);
  const gb = useGrowthBook();

  const { data: org, isLoading: isLoadingOrgEntitlements } = useGetOrgQuery(
    activeOrgId ?? skipToken
  );

  const onboardingRequired = org?.onboardingRequired;

  const activePlan = org?.entitlementPlan;

  const growthbookEntitlements = getGrowthbookValues({
    gb,
    appPrefix: 'appentitlement',
    featurePrefix: 'featureentitlement',
  });

  const { defaultTeamTypeName, defaultTeamTypeNamePlural } =
    useCustomTeamTypeName();

  const appPermissions = useMemo(() => {
    const permissions: { [key in ALL_APP_IDS]?: AppPermission } = {};

    // Don't calculate permissions until user access controls are loaded
    if (isLoadingUserAccessControls || !userAccessControls) {
      return permissions;
    }

    // calculate permissions for all other apps
    Object.values(ALL_APPS).forEach((app) => {
      if (!(app.id in permissions)) {
        const accessLevel = _.get(
          userAccessControls,
          _.camelCase(app.id.toLowerCase()),
          appPermissionAccessLevels.none
        ) as appPermissionAccessLevels;
        const foundEntitlement = activePlan?.apps?.find(
          (e) => e.slug?.toLowerCase() === app.id?.toLowerCase()
        ); // app ids in entitlements are lowercase
        const permissionStateWithEntitlements = calculateAppPermissionState({
          app,
          entitlement: foundEntitlement,
          growthbookEntitlement: growthbookEntitlements.apps[app.id],
          accessLevel,
          blended,
          isCofactr,
          onboardingRequired,
        });
        permissions[app.id as keyof typeof ALL_APP_IDS] = {
          id: app.id,
          state: permissionStateWithEntitlements,
          comingSoon: app.globalComingSoon || false,
          accessLevel,
        };
      }
    });
    return permissions;
  }, [
    isLoadingUserAccessControls,
    userAccessControls,
    activePlan,
    growthbookEntitlements,
    blended,
    isCofactr,
    onboardingRequired,
  ]);

  const appsWithPermissions = useMemo(() => {
    const apps: { [key: string]: App } = {};

    Object.values(ALL_APPS).forEach((app) => {
      apps[app.id] = { ...app, permissions: appPermissions[app.id] };
    });

    // calculate permissions for views + features
    Object.values(apps).forEach((app) => {
      if (app.views) {
        Object.values(app.views).forEach((view) => {
          const accessLevel = _.get(
            userAccessControls,
            _.camelCase(app.id.toLowerCase())
          );
          const foundEntitlement = activePlan?.features?.find(
            (e) =>
              e.slug?.toLowerCase() ===
              view?.inheritedFeatureEntitlement?.toLowerCase()
          );
          const permissionStateWithEntitlements =
            calculateViewFeaturePermissionState({
              viewOrFeature: view,
              parentAppState: _.get(apps, [app.id, 'permissions', 'state']),
              entitlement: foundEntitlement,
              growthbookEntitlement: view?.inheritedFeatureEntitlement
                ? growthbookEntitlements.features[
                    view?.inheritedFeatureEntitlement
                  ]
                : undefined,
              blended,
              isStaff: (user?.isStaff || false) && (viewAsStaff || false),
            });
          _.set(apps, [app.id, 'views', view.id, 'permissions'], {
            id: view.id,
            state:
              permissionStateWithEntitlements ===
              appPermissionStates.enabledInStaffMode
                ? appPermissionStates.enabled
                : permissionStateWithEntitlements,
            comingSoon: view.globalComingSoon || false,
            accessLevel,
            enabledDueToStaffMode:
              permissionStateWithEntitlements ===
              appPermissionStates.enabledInStaffMode,
          });

          // Override 'Teams' naming if the user has changed defaultTeamType
          if (view.id === ALL_VIEW_IDS.TEAMS_TABLE) {
            _.set(
              apps,
              [app.id, 'views', view.id, 'name'],
              defaultTeamTypeNamePlural
            );
          }
          if (view.id === ALL_VIEW_IDS.TEAMS_PREFERENCES) {
            _.set(
              apps,
              [app.id, 'views', view.id, 'name'],
              `${defaultTeamTypeName} Preferences`
            );
          }
        });
      }

      if (app.features) {
        Object.values(app.features).forEach((feature) => {
          const accessLevel = _.get(
            userAccessControls,
            _.camelCase(app.id.toLowerCase())
          );
          const foundEntitlement = activePlan?.features?.find(
            (e) => e.slug?.toLowerCase() === feature.id?.toLowerCase() // feature ids in entitlements are lowercase
          );
          const permissionStateWithEntitlements =
            calculateViewFeaturePermissionState({
              viewOrFeature: feature,
              parentAppState: _.get(apps, [app.id, 'permissions', 'state']),
              entitlement: foundEntitlement,
              growthbookEntitlement:
                growthbookEntitlements.features[feature.id],
            });
          _.set(apps, [app.id, 'features', feature.id, 'permissions'], {
            id: feature.id,
            state: permissionStateWithEntitlements,
            comingSoon: feature.globalComingSoon || false,
            accessLevel,
          });
        });
      }
    });

    return apps;
  }, [
    appPermissions,
    activePlan,
    growthbookEntitlements,
    defaultTeamTypeName,
    defaultTeamTypeNamePlural,
    blended,
    userAccessControls,
    user?.isStaff,
    viewAsStaff,
  ]);

  const hasAppPermission = useCallback(
    (app: keyof typeof ALL_APP_IDS) =>
      Boolean(
        appPermissions[app] &&
          appPermissions[app]?.state === appPermissionStates.enabled
      ),
    [appPermissions]
  );

  const getParentApp = ({
    id,
    type,
  }: {
    id: keyof typeof ALL_FEATURE_IDS | keyof typeof ALL_VIEW_IDS;
    type: VIEW_OR_FEATURE;
  }) => {
    const appKeys = {
      [VIEW_OR_FEATURE.VIEW]: 'views',
      [VIEW_OR_FEATURE.FEATURE]: 'features',
    };
    return Object.values(ALL_APPS).find(
      (curApp) =>
        Boolean(curApp?.[appKeys[type] as keyof App]) &&
        Object.keys(curApp?.[appKeys[type] as keyof App] || {}).includes(id)
    );
  };

  const hasFeaturePermission = useCallback(
    (feature: keyof typeof ALL_FEATURE_IDS) => {
      const parentApp = getParentApp({
        id: feature,
        type: VIEW_OR_FEATURE.FEATURE,
      });
      return parentApp
        ? Boolean(
            appsWithPermissions[parentApp.id]?.features?.[feature]?.permissions
              ?.state === appPermissionStates.enabled
          )
        : false;
    },
    [appsWithPermissions]
  );

  const hasViewPermission = useCallback(
    (view: keyof typeof ALL_VIEW_IDS) => {
      const parentApp = getParentApp({ id: view, type: VIEW_OR_FEATURE.VIEW });
      return parentApp
        ? Boolean(
            appsWithPermissions[parentApp.id]?.views?.[view]?.permissions
              ?.state === appPermissionStates.enabled
          )
        : false;
    },
    [appsWithPermissions]
  );

  // can probably be combined with isFeatureDisabled but leaving separate for now
  const isViewDisabled = useCallback(
    ({
      view,
      requiredPermissionLevel,
    }: {
      view: keyof typeof ALL_VIEW_IDS;
      requiredPermissionLevel?: keyof typeof appPermissionAccessLevels;
    }) => {
      const parentApp = getParentApp({ id: view, type: VIEW_OR_FEATURE.VIEW });

      // if parent app is disabled, feature is disabled
      if (parentApp && !hasAppPermission(parentApp?.id)) {
        return true;
      }

      // if view doesn't have sufficient permissions, view is disabled
      if (!hasViewPermission(view)) {
        return true;
      }

      // if required permission provided and parent app doesn't have sufficient permission, view is disabled
      // if required permission provided and view doesn't have sufficient permission, view is disabled
      if (requiredPermissionLevel) {
        const parentAppHasPermissionLevel =
          parentApp &&
          doesUserAccessLevelMeetRequiredAccessLevel({
            userPermissionLevel:
              appsWithPermissions[parentApp?.id]?.permissions?.accessLevel,
            requiredPermissionLevel,
          });

        const viewHasPermissionLevel =
          parentApp &&
          doesUserAccessLevelMeetRequiredAccessLevel({
            userPermissionLevel:
              appsWithPermissions[parentApp?.id]?.views?.[view]?.permissions
                ?.accessLevel,
            requiredPermissionLevel,
          });

        return !(parentAppHasPermissionLevel && viewHasPermissionLevel);
      }

      // otherwise, view has sufficient permission, view isn't disabled
      return false;
    },
    [appsWithPermissions, hasAppPermission, hasViewPermission]
  );

  const isFeatureDisabled = useCallback(
    ({
      feature,
      requiredPermissionLevel,
    }: {
      feature: keyof typeof ALL_FEATURE_IDS;
      requiredPermissionLevel?: keyof typeof appPermissionAccessLevels;
    }) => {
      const parentApp = getParentApp({
        id: feature,
        type: VIEW_OR_FEATURE.FEATURE,
      });

      // if parent app is disabled, feature is disabled
      if (parentApp && !hasAppPermission(parentApp?.id)) {
        return true;
      }

      // if feature doesn't have sufficient permissions, feature is disabled
      if (!hasFeaturePermission(feature)) {
        return true;
      }

      // if required permission provided and parent app doesn't have sufficient permission, feature is disabled
      // if required permission provided and feature doesn't have sufficient permission, feature is disabled
      if (requiredPermissionLevel) {
        const parentAppHasPermissionLevel =
          parentApp &&
          doesUserAccessLevelMeetRequiredAccessLevel({
            userPermissionLevel:
              appsWithPermissions[parentApp?.id]?.permissions?.accessLevel,
            requiredPermissionLevel,
          });

        const featureHasPermissionLevel =
          parentApp &&
          doesUserAccessLevelMeetRequiredAccessLevel({
            userPermissionLevel:
              appsWithPermissions[parentApp?.id]?.features?.[feature]
                ?.permissions?.accessLevel,
            requiredPermissionLevel,
          });

        return !(parentAppHasPermissionLevel && featureHasPermissionLevel);
      }

      // otherwise, feature has sufficient permission, feature isn't disabled
      return false;
    },
    [appsWithPermissions, hasAppPermission, hasFeaturePermission]
  );

  // provided a PermissionScope (app and optionally feature) determines if permissions are disabled
  // if no scope is provided, permissions implicitly are not disabled
  const isPermissionDisabled = useCallback(
    (scope?: PermissionScope) => {
      const convertedPermissionScope = getPartsV2PermissionScope({
        permissionScope: scope,
        tempParts2Ui,
      });
      //  if no app is scoped, permissions cannot be disabled
      if (!convertedPermissionScope) {
        return false;
      }
      const appIsEnabled = hasAppPermission(convertedPermissionScope.app);
      // if app isn't enabled, permissions are disabled
      if (!appIsEnabled) {
        return true;
      }

      // if access level is provided, and user doesn't have correct permissions, feature is disabled
      if (
        !doesUserAccessLevelMeetRequiredAccessLevel({
          userPermissionLevel:
            appsWithPermissions[convertedPermissionScope.app]?.permissions
              ?.accessLevel,
          requiredPermissionLevel: convertedPermissionScope?.accessLevel,
        })
      ) {
        return true;
      }

      // if view is provided and view is disabled, permissions are disabled
      if (
        convertedPermissionScope.view &&
        isViewDisabled({
          view: convertedPermissionScope.view,
          requiredPermissionLevel: convertedPermissionScope?.accessLevel,
        })
      ) {
        return true;
      }

      // if there's a feature within the scope of the app, return its permissions.
      // otherwise, permissions are not disabled
      return convertedPermissionScope.feature
        ? isFeatureDisabled({
            feature: convertedPermissionScope.feature,
            requiredPermissionLevel: convertedPermissionScope?.accessLevel,
          })
        : false;
    },
    [
      appsWithPermissions,
      hasAppPermission,
      isFeatureDisabled,
      isViewDisabled,
      tempParts2Ui,
    ]
  );

  const getDisabledReason = useCallback(
    (permissionScope: PermissionScope) => {
      const convertedPermissionScope = getPartsV2PermissionScope({
        permissionScope,
        tempParts2Ui,
      }) as PermissionScope;
      if (!hasAppPermission(convertedPermissionScope.app)) {
        return {
          disabledType: DISABLED_TYPE.APP,
          name: ALL_APPS[convertedPermissionScope.app].name,
          permissionState:
            appsWithPermissions[convertedPermissionScope.app]?.permissions
              ?.state,
        };
      }

      const viewParentApp =
        convertedPermissionScope?.view &&
        getParentApp({
          id: convertedPermissionScope?.view,
          type: VIEW_OR_FEATURE.VIEW,
        });

      // if there's a view within the scope of the app, and its parent app is disabled
      // return that as the disabled reason
      if (viewParentApp && !hasAppPermission(viewParentApp.id)) {
        return {
          disabledType: DISABLED_TYPE.PARENT_APP,
          name: viewParentApp.name,
          permissionState:
            appsWithPermissions[viewParentApp.id]?.permissions?.state,
        };
      }

      // if there's a view within the scope of the app, and it's disabled
      // return that as the disabled reason
      if (
        convertedPermissionScope?.view &&
        isViewDisabled({
          view: convertedPermissionScope?.view,
          requiredPermissionLevel: convertedPermissionScope?.accessLevel,
        })
      ) {
        return {
          disabledType: DISABLED_TYPE.VIEW,
          name: viewParentApp?.views?.[convertedPermissionScope.view]?.name,
          permissionState: viewParentApp
            ? appsWithPermissions[viewParentApp?.id]?.views?.[
                convertedPermissionScope.view
              ]?.permissions?.state
            : appPermissionStates.unavailable,
        };
      }

      const featureParentApp =
        convertedPermissionScope?.feature &&
        getParentApp({
          id: convertedPermissionScope.feature,
          type: VIEW_OR_FEATURE.FEATURE,
        });

      // if there's a feature within the scope of the app, and its parent app is disabled
      // return that as the disabled reason
      if (
        convertedPermissionScope?.feature &&
        featureParentApp &&
        !hasAppPermission(featureParentApp.id)
      ) {
        return {
          disabledType: DISABLED_TYPE.PARENT_APP,
          name: featureParentApp.name,
          permissionState:
            appsWithPermissions[featureParentApp.id]?.permissions?.state,
        };
      }

      // if there's a feature within the scope of the app, and it's disabled
      // return that as the disabled reason
      if (
        convertedPermissionScope?.feature &&
        !hasFeaturePermission(convertedPermissionScope.feature)
      ) {
        return {
          disabledType: DISABLED_TYPE.FEATURE,
          name: featureParentApp?.features?.[convertedPermissionScope.feature]
            ?.name,
          permissionState: featureParentApp
            ? appsWithPermissions[featureParentApp?.id]?.features?.[
                convertedPermissionScope.feature
              ]?.permissions?.state
            : appPermissionStates.unavailable,
        };
      }

      // as a default/fallback, return 'app' as the disabled reason
      return { disabledType: DISABLED_TYPE.APP };
    },
    [
      appsWithPermissions,
      hasAppPermission,
      hasFeaturePermission,
      isViewDisabled,
      tempParts2Ui,
    ]
  );

  const filteredAppPermissions = useMemo(() => {
    if (tempParts2Ui) {
      return _.omit(appPermissions, PARTS_V1_DEPRECATIONS.apps);
    }
    return _.omit(appPermissions, [ALL_APP_IDS.PARTS]);
  }, [appPermissions, tempParts2Ui]);

  const filteredAppsWithPermissions = useMemo(() => {
    if (tempParts2Ui) {
      return _.omit(appsWithPermissions, PARTS_V1_DEPRECATIONS.apps);
    }
    return _.omit(appsWithPermissions, [ALL_APP_IDS.PARTS]);
  }, [appsWithPermissions, tempParts2Ui]);

  return (
    <AppAccessContext.Provider
      value={{
        appPermissions: filteredAppPermissions,
        appsWithPermissions: filteredAppsWithPermissions,
        isLoading:
          isLoadingUser ||
          isLoadingUserAccessControls ||
          !userAccessControls ||
          isLoadingOrgEntitlements,
        hasAppPermission,
        hasFeaturePermission,
        isFeatureDisabled,
        isPermissionDisabled,
        getDisabledReason,
      }}
    >
      {children}
    </AppAccessContext.Provider>
  );
};
